import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Clock as ClockIcon, Loader2, LogIn, LogOut, MapPin, AlertTriangle } from "lucide-react";
import { api } from "@/lib/api";
import { usePermissions } from "@/lib/permissions";
import { currentLocation, mapLink } from "@/lib/geo";
import { PageHeader } from "@/components/PageHeader";
import { EmptyState } from "@/components/EmptyState";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "@/components/ui/table";
import { getErrorMessage } from "@/lib/errors";

const STATUS = { PRESENT: "Present", HALF_DAY: "Half day", ABSENT: "Absent", WEEKLY_OFF: "Weekly off", HOLIDAY: "Holiday", PAID_LEAVE: "Paid leave", UNPAID_LEAVE: "Unpaid leave" };
const LOC_NOTE = { DENIED: "location refused", UNAVAILABLE: "location unavailable", NONE: "no location" };
const todayISO = () => new Date().toLocaleDateString("en-CA");

/** Clock in / out: your own button (when your login is tied to an employee), and the crew board for supervisors and HR. */
export default function Clock() {
  const { can } = usePermissions();
  const canBoard = can("ATTENDANCE", "view");
  const canClockOthers = can("ATTENDANCE", "create");
  return (
    <div className="space-y-5" data-testid="clock-page">
      <PageHeader title="Clock in / out" description="Clock in when you start and out when you finish. The attendance sheet is marked from it, using the working hours your administrator set." />
      <MyClock />
      {canBoard && <CrewBoard canClock={canClockOthers} />}
    </div>
  );
}

function MyClock() {
  const [me, setMe] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = useCallback(async () => { try { setMe((await api.get("/hr/clock/me")).data); } catch (err) { toast.error(getErrorMessage(err)); } }, []);
  useEffect(() => { load(); }, [load]);
  if (!me) return <p className="text-sm text-muted-foreground">Loading…</p>;
  if (!me.linked) {
    return <div className="max-w-xl rounded-xl border border-border bg-card p-5 text-sm text-muted-foreground" data-testid="clock-not-linked">
      Your login is not tied to an employee record yet, so you cannot clock in. Ask HR to link it on the Employees page.</div>;
  }
  const t = me.today || {};
  const onLeave = ["PAID_LEAVE", "UNPAID_LEAVE"].includes(t.status) && !t.clock_in;
  const kind = onLeave ? null : !t.clock_in ? "IN" : !t.clock_out ? "OUT" : null;
  const punch = async () => {
    setBusy(true);
    try {
      const loc = await currentLocation();
      const { data } = await api.post("/hr/clock/me", { kind, ...loc });
      const where = loc.location_status === "OK" ? "" : ` (${LOC_NOTE[loc.location_status]}; HR will see that)`;
      toast.success(kind === "IN" ? `Clocked in at ${data.time}${where}` : `Clocked out at ${data.time}: ${STATUS[data.status] || data.status}${where}`);
      await load();
    } catch (err) { toast.error(getErrorMessage(err)); } finally { setBusy(false); }
  };
  return (
    <div className="max-w-xl space-y-3 rounded-xl border border-border bg-card p-5" data-testid="my-clock">
      <div className="flex items-center justify-between gap-3">
        <div><p className="text-sm font-medium">{me.employee.name} <span className="font-mono text-xs text-muted-foreground">{me.employee.code}</span></p>
          <p className="text-xs text-muted-foreground">Shift {me.policy.shift_start}–{me.policy.shift_end} · full day {Number(me.policy.full_day_hours)} h</p></div>
        <ClockIcon className="h-6 w-6 text-[#910029]" />
      </div>
      <div className="grid grid-cols-3 gap-2 text-center text-sm" data-testid="my-clock-today">
        <div className="rounded-lg border p-2"><p className="text-[11px] text-muted-foreground">In</p><p className="font-mono">{t.clock_in || "—"}</p></div>
        <div className="rounded-lg border p-2"><p className="text-[11px] text-muted-foreground">Out</p><p className="font-mono">{t.clock_out || "—"}</p></div>
        <div className="rounded-lg border p-2"><p className="text-[11px] text-muted-foreground">Today</p><p>{STATUS[t.status] || "—"}</p></div>
      </div>
      {kind ? (
        <Button className="h-12 w-full text-base" disabled={busy} onClick={punch} data-testid="my-clock-button">
          {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : kind === "IN" ? <LogIn className="h-5 w-5" /> : <LogOut className="h-5 w-5" />}
          {kind === "IN" ? "Clock in" : "Clock out"}</Button>
      ) : onLeave ? <p className="text-center text-sm text-muted-foreground" data-testid="my-clock-on-leave">You are on approved leave today. If you are working after all, ask HR to cancel the leave first.</p>
        : <p className="text-center text-sm text-muted-foreground" data-testid="my-clock-done">You have clocked in and out today.</p>}
      <p className="flex items-center gap-1.5 text-[11px] text-muted-foreground"><MapPin className="h-3 w-3" /> Your phone's location is saved with each clock-in and clock-out. If you refuse it, you can still clock in; HR sees that it is missing.</p>
    </div>
  );
}

function Where({ loc }) {
  if (!loc) return null;
  if (loc.status === "OK" && loc.latitude) {
    const rough = (loc.accuracy_m || 0) > 1000;                 // usually a computer guessing from its network, not a phone's GPS
    return <a href={mapLink(loc.latitude, loc.longitude)} target="_blank" rel="noreferrer" className={`inline-flex items-center gap-0.5 text-xs underline ${rough ? "text-amber-700" : "text-primary"}`}
      title={rough ? `Approximate: within about ${Math.round(loc.accuracy_m / 1000)} km` : `Within about ${loc.accuracy_m || "?"} m`}><MapPin className="h-3 w-3" />{rough ? "approx." : "map"}</a>;
  }
  return <span className="text-xs text-amber-700">{LOC_NOTE[loc.status] || "no location"}</span>;
}

function CrewBoard({ canClock }) {
  const [day, setDay] = useState(todayISO());
  const [rows, setRows] = useState(null);
  const [q, setQ] = useState("");
  const [busyId, setBusyId] = useState(null);
  const load = useCallback(async () => {
    try { setRows((await api.get("/hr/clock/board", { params: { on: day } })).data.rows); } catch (err) { toast.error(getErrorMessage(err)); setRows([]); }
  }, [day]);
  useEffect(() => { load(); }, [load]);
  const isToday = day === todayISO();
  const punch = async (r, kind) => {
    setBusyId(r.employee_id);
    try {
      const loc = await currentLocation();
      const { data } = await api.post(`/hr/clock/${r.employee_id}`, { kind, ...loc });
      toast.success(`${r.name}: ${kind === "IN" ? "clocked in" : "clocked out"} at ${data.time}`);
      await load();
    } catch (err) { toast.error(getErrorMessage(err)); } finally { setBusyId(null); }
  };
  const shown = (rows || []).filter((r) => !q || `${r.name} ${r.code}`.toLowerCase().includes(q.toLowerCase()));
  return (
    <div className="space-y-3 rounded-xl border border-border bg-card p-5" data-testid="crew-board">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div><p className="text-sm font-medium">Crew board</p><p className="text-xs text-muted-foreground">Who clocked in and out. {canClock ? "Clock in someone who has no login (it is recorded as done by you, with your location)." : ""}</p></div>
        <div className="flex gap-2">
          <Input type="date" value={day} max={todayISO()} onChange={(e) => setDay(e.target.value)} className="h-9 w-40" data-testid="crew-board-date" />
          <Input placeholder="Search" value={q} onChange={(e) => setQ(e.target.value)} className="h-9 w-40" data-testid="crew-board-search" />
        </div>
      </div>
      {rows === null ? <p className="text-sm text-muted-foreground">Loading…</p> : !shown.length ? <EmptyState title="No employees" description="Add employees on the Employees page." testId="crew-board-empty" /> : (
        <Table>
          <TableHeader><TableRow><TableHead>Employee</TableHead><TableHead>In</TableHead><TableHead>Out</TableHead><TableHead>Day</TableHead><TableHead>Needs a look</TableHead><TableHead /></TableRow></TableHeader>
          <TableBody>{shown.map((r) => (
            <TableRow key={r.employee_id} data-testid={`crew-row-${r.code}`}>
              <TableCell><span className="text-foreground">{r.name}</span> <span className="font-mono text-xs text-muted-foreground">{r.code}</span>{!r.has_login && <span className="ml-1 text-[10px] text-muted-foreground">(no login)</span>}</TableCell>
              <TableCell className="font-mono text-xs">{r.clock_in || "—"} <Where loc={r.in_location} />{r.in_location?.source === "SUPERVISOR" && <span className="ml-1 text-[10px] text-muted-foreground">by supervisor</span>}</TableCell>
              <TableCell className="font-mono text-xs">{r.clock_out || "—"} <Where loc={r.out_location} /></TableCell>
              <TableCell className="text-xs">{STATUS[r.status] || "—"}{Number(r.overtime_hours) > 0 && <span className="ml-1 text-muted-foreground">+{Number(r.overtime_hours)} h OT</span>}</TableCell>
              <TableCell className="text-xs" data-testid={`crew-flags-${r.code}`}>{r.flags.includes("NO_CLOCK_OUT") && <span className="mr-2 inline-flex items-center gap-1 text-rose-700"><AlertTriangle className="h-3 w-3" />No clock-out</span>}
                {r.flags.includes("NO_LOCATION") && <span className="text-amber-700">Location missing</span>}
                {r.flags.includes("ROUGH_LOCATION") && <span className="text-amber-700">Location approximate</span>}</TableCell>
              <TableCell className="text-right">{canClock && isToday && !["PAID_LEAVE", "UNPAID_LEAVE"].includes(r.status) && (!r.clock_in
                ? <Button size="sm" variant="outline" disabled={busyId === r.employee_id} onClick={() => punch(r, "IN")} data-testid={`crew-in-${r.code}`}>Clock in</Button>
                : !r.clock_out ? <Button size="sm" variant="outline" disabled={busyId === r.employee_id} onClick={() => punch(r, "OUT")} data-testid={`crew-out-${r.code}`}>Clock out</Button> : null)}</TableCell>
            </TableRow>))}</TableBody>
        </Table>)}
      <p className="text-[11px] text-muted-foreground">A missing clock-out is not guessed: correct the day on the Attendance page, with a reason.</p>
    </div>
  );
}
